import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, ArrowUpRight, ArrowDownRight, Minus, Scale, Layers } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const VariationBadge = ({ variation, inverse = false }) => {
  const delta = variation?.delta ?? 0;
  const pct = variation?.pct;
  const isPositive = delta > 0;
  const isNegative = delta < 0;
  // inverse=true → la croissance est "mauvaise" (utilisé pour HT impôt = bonne si baisse ?)
  const goodColor = inverse ? 'text-rose-700 bg-rose-50 border-rose-200' : 'text-emerald-700 bg-emerald-50 border-emerald-200';
  const badColor = inverse ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200';
  const color = isPositive ? goodColor : isNegative ? badColor : 'text-slate-600 bg-slate-50 border-slate-200';
  const Icon = isPositive ? ArrowUpRight : isNegative ? ArrowDownRight : Minus;
  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-bold ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{delta > 0 ? '+' : ''}{fmt(delta)} €</span>
      {pct !== null && pct !== undefined && (
        <span className="opacity-70">({pct > 0 ? '+' : ''}{pct}%)</span>
      )}
    </div>
  );
};

export const CompareView = ({ data, fmt }) => {
  const { current, previous, variations, by_source_compare, by_libelle_compare, monthly_compare, quarterly_compare } = data;
  const yearN = current?.year || current?.period?.start?.slice(0, 4) || 'N';
  const yearN1 = previous?.year || previous?.period?.start?.slice(0, 4) || 'N-1';

  // Données pour le graphique mois × mois
  const chartData = (monthly_compare || []).map((m) => ({
    mois: m.label,
    [yearN]: m.current_taxe,
    [yearN1]: m.previous_taxe,
  }));

  const tooltipFormatter = (value) => `${Number(value || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <div className="space-y-5">
      {/* KPIs globaux comparés */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-emerald-50 to-teal-50" data-testid="compare-kpi-taxe">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Taxes collectées</div>
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="text-[10px] text-slate-500">{yearN}</div>
              <div className="text-2xl font-bold text-teal-700 tabular-nums">{fmt(current.total_taxe_global)} €</div>
              <div className="text-[10px] text-slate-400 mt-1">{yearN1} : {fmt(previous.total_taxe_global)} €</div>
            </div>
            <VariationBadge variation={variations.total_taxe} />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="compare-kpi-ht">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">HT taxé</div>
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="text-[10px] text-slate-500">{yearN}</div>
              <div className="text-2xl font-bold text-blue-700 tabular-nums">{fmt(current.total_ht_global)} €</div>
              <div className="text-[10px] text-slate-400 mt-1">{yearN1} : {fmt(previous.total_ht_global)} €</div>
            </div>
            <VariationBadge variation={variations.total_ht} />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 bg-gradient-to-br from-purple-50 to-fuchsia-50" data-testid="compare-kpi-docs">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Documents</div>
          <div className="flex items-end justify-between gap-2">
            <div>
              <div className="text-[10px] text-slate-500">{yearN}</div>
              <div className="text-2xl font-bold text-purple-700 tabular-nums">{current.nb_documents_distinct}</div>
              <div className="text-[10px] text-slate-400 mt-1">{yearN1} : {previous.nb_documents_distinct}</div>
            </div>
            <VariationBadge variation={variations.nb_documents} />
          </div>
        </div>
      </div>

      {/* Graphique en barres : évolution mensuelle */}
      {chartData.length > 0 && (
        <div data-testid="compare-monthly-chart">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> Évolution mensuelle des taxes collectées
            <span className="text-[10px] font-normal text-slate-500 normal-case tracking-normal ml-2">— repérer les pics saisonniers</span>
          </div>
          <div className="rounded-xl border border-slate-200 p-3 bg-white" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k€`} />
                <Tooltip
                  formatter={tooltipFormatter}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                  labelStyle={{ fontWeight: 'bold', color: '#0f172a' }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Bar dataKey={yearN1} fill="#94a3b8" radius={[4, 4, 0, 0]} name={`${yearN1} (N-1)`} />
                <Bar dataKey={yearN} fill="#0d9488" radius={[4, 4, 0, 0]} name={`${yearN} (N)`} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tendance trimestrielle */}
      {quarterly_compare?.length > 0 && (
        <div data-testid="compare-quarterly">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-blue-600" /> Tendance trimestrielle
            <span className="text-[10px] font-normal text-slate-500 normal-case tracking-normal ml-2">— anticiper vos déclarations fiscales</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quarterly_compare.map((q) => {
              const pct = q.variation?.pct;
              const isUp = (q.variation?.delta || 0) > 0;
              const isDown = (q.variation?.delta || 0) < 0;
              const gradient = isUp ? 'from-emerald-500 to-teal-600' : isDown ? 'from-rose-500 to-pink-600' : 'from-slate-400 to-slate-500';
              return (
                <div key={q.quarter} className={`relative overflow-hidden rounded-xl p-4 text-white bg-gradient-to-br ${gradient} shadow-md`} data-testid={`compare-quarter-${q.quarter}`}>
                  <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/15" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-bold tracking-wider">{q.quarter}</div>
                      {isUp && <ArrowUpRight className="w-4 h-4" />}
                      {isDown && <ArrowDownRight className="w-4 h-4" />}
                    </div>
                    <div className="text-lg font-bold tabular-nums">{fmt(q.current_taxe)} €</div>
                    <div className="text-[10px] opacity-90 mt-1">N-1 : {fmt(q.previous_taxe)} €</div>
                    <div className="mt-2 pt-2 border-t border-white/25 text-[11px] font-semibold">
                      {q.variation?.delta > 0 ? '+' : ''}{fmt(q.variation?.delta || 0)} €
                      {pct !== null && pct !== undefined && (
                        <span className="opacity-80 ml-1">({pct > 0 ? '+' : ''}{pct}%)</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Par source */}
      {by_source_compare?.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-2">
            <Scale className="w-3.5 h-3.5 text-amber-600" /> Évolution par source
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Source</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">{yearN1}</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">{yearN}</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">Variation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {by_source_compare.map((s) => (
                  <tr key={s.source} data-testid={`compare-source-${s.source.replace(/\W+/g,'_')}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{s.source}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmt(s.previous.total_taxe)} €</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-teal-700">{fmt(s.current.total_taxe)} €</td>
                    <td className="px-3 py-2 text-right"><VariationBadge variation={s.variation_taxe} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top libellés */}
      {by_libelle_compare?.length > 0 && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2 flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-purple-600" /> Évolution par taxe (top {by_libelle_compare.length})
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-bold text-slate-600 uppercase">Libellé</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">{yearN1}</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">{yearN}</th>
                  <th className="text-right px-3 py-2 text-xs font-bold text-slate-600 uppercase">Variation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {by_libelle_compare.map((b) => (
                  <tr key={b.libelle} className="hover:bg-slate-50/50">
                    <td className="px-3 py-2 text-slate-800">{b.libelle}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmt(b.previous.total_taxe)} €</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-teal-700">{fmt(b.current.total_taxe)} €</td>
                    <td className="px-3 py-2 text-right"><VariationBadge variation={b.variation_taxe} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="text-[11px] text-slate-400 text-center pt-2 border-t border-slate-100">
        Période N : du {current.period.start} au {current.period.end} · Période N-1 : du {previous.period.start} au {previous.period.end}
      </div>
    </div>
  );
};
